import _ from 'lodash';

import type { Table } from '@adflow/shared/util-js';

import type { PermissionMetadata } from './permission-metadata';
import type { PermissionSystem } from './permission-system';
import type { PermissionKey, PermissionTable } from './permission-types';

export interface PermissionHelpers<P extends string, A extends string> {
	isPermissionType(value: string): value is P;
	isActionType(value: string): value is A;
	isValidPermissionString(value: string): boolean;

	/**
	 * Technically this function is just a wrapper around
	 * `system.permissionsMetadata[permission][action]`,
	 * but it makes TypeScript happy
	 */
	getMetadata(permission: P, action: A): PermissionMetadata<P, A>;

	tableToArray(permissionsTable: PermissionTable<P, A>): PermissionKey<P, A>[];
	compressTable(permissionsTable: PermissionTable<P, A>): PermissionTable<P, A>;
	combineTables(
		permissionsTables: PermissionTable<P, A>[]
	): PermissionTable<P, A>;
	normalizeTable(permissionsTable: PermissionTable<P, A>): Table<P, A, boolean>;

	hasAllPermissions(
		permissionsTable: PermissionTable<P, A>,
		permissionStrings: `${P}:${A}`[]
	): boolean;
}

export function makePermissionHelpers<P extends string, A extends string>(
	system: PermissionSystem<P, A>
): PermissionHelpers<P, A> {
	function isPermissionType(value?: string): value is P {
		return system.permissionTypes.includes(value as P);
	}

	function isActionType(value?: string): value is A {
		return system.actionTypes.includes(value as A);
	}

	function tableToArray(
		permissionsTable: PermissionTable<P, A>
	): PermissionKey<P, A>[] {
		const keyArray: PermissionKey<P, A>[] = [];

		for (const p of system.permissionTypes) {
			for (const a of system.actionTypes) {
				if (permissionsTable?.[p]?.[a]) {
					keyArray.push([p, a]);
				}
			}
		}

		return keyArray;
	}

	function getMetadata(permission: P, action: A): PermissionMetadata<P, A> {
		return system.permissionsMetadata[permission]?.[action] ?? 'unused';
	}

	function compressTable(
		permissionsTable: PermissionTable<P, A>
	): PermissionTable<P, A> {
		const result: PermissionTable<P, A> = {};

		for (const p of system.permissionTypes) {
			for (const a of system.actionTypes) {
				if (permissionsTable?.[p]?.[a]) {
					_.set(result, [p, a], true);
				}
			}
		}

		return result;
	}

	function combineTables(
		permissionsTables: PermissionTable<P, A>[]
	): PermissionTable<P, A> {
		const result: PermissionTable<P, A> = {};

		for (const p of system.permissionTypes) {
			for (const a of system.actionTypes) {
				if (permissionsTables.some((pt) => pt[p]?.[a])) {
					_.set(result, [p, a], true);
				}
			}
		}

		return result;
	}

	function normalizeTable(
		permissionsTable: PermissionTable<P, A>
	): Table<P, A, boolean> {
		const result = {} as Table<P, A, boolean>;

		for (const p of system.permissionTypes) {
			for (const a of system.actionTypes) {
				_.set(result, [p, a], permissionsTable?.[p]?.[a] ?? false);
			}
		}

		return result;
	}

	function isValidPermissionString(value: string): boolean {
		const [p, a] = value.split(':');
		return (
			isPermissionType(p) &&
			isActionType(a) &&
			system.permissionsMetadata[p][a] !== 'unused'
		);
	}

	function hasAllPermissions(
		permissionsTable: PermissionTable<P, A> | undefined,
		permissionStrings: `${P}:${A}`[]
	): boolean {
		// permissionStrings is something like ['Customers:create', 'Users:list']
		for (const permissionString of permissionStrings) {
			const [p, a] = permissionString.split(':');

			if (!isPermissionType(p)) {
				throw new Error(`${p} is not a recognized permission`);
			}

			if (!isActionType(a)) {
				throw new Error(`${a} is not a recognized permission action`);
			}

			// If the permission is marked as unused, then it is not required.
			if (system.permissionsMetadata[p]?.[a] === 'unused') {
				continue;
			}

			if (!permissionsTable?.[p]?.[a]) {
				return false;
			}
		}

		return true;
	}

	return {
		isPermissionType,
		isActionType,
		isValidPermissionString,

		getMetadata,

		tableToArray,
		compressTable,
		combineTables,
		normalizeTable,

		hasAllPermissions,
	};
}
